import React, { useEffect, useRef, useState } from "react";
import { Group, Stack, Text } from "@mantine/core";
import { IconAlertTriangle, IconCircleCheck } from "@tabler/icons";
import { useTranslation } from "react-i18next";
import { useServerApi } from "../hooks/userServerApi";
import {
  COUNTRY_RISK_LEVEL_COLOR,
  countryScreeningRank,
  formatRiskScore,
  normalizeCountryRiskRatings,
  screenCountryRisk,
} from "../helpers/countryRiskRating";
import {
  assignDueDiligenceCountry,
  dueDiligenceCountryValue,
  resolveClientDueDiligenceChecks,
  resolveClientDueDiligenceCountries,
} from "../helpers/clientDueDiligenceValues";
import {
  DueDiligenceBadge,
  DueDiligenceFacts,
  DueDiligencePanel,
} from "./clientDueDiligencePanel";

const screeningPresentation = (screening, t) => {
  const missing = !screening?.country || screening.status === "EMPTY";
  const listed = !missing && screening.status !== "NOT_LISTED";
  const pass = screening?.status === "PASS";

  const badge = missing
    ? { color: "gray", label: t("Not ready") }
    : screening.status === "NOT_LISTED"
      ? { color: "red", label: t("Not on the list") }
      : pass
        ? { color: "green", label: t("Passed") }
        : screening.status === "FURTHER_HANDLING"
          ? { color: "orange", label: t("Needs handling") }
          : null;

  const note = missing
    ? t("Use the countries already filled in Basic Info or Contact Info.")
    : screening.status === "NOT_LISTED"
      ? t(
          "This country is not on the Country Risk Rating List. Request further handling by Operation."
        )
      : listed
        ? pass
          ? t("Pass (screening result Low/Medium)")
          : t(
              "Request further handling by Operation (screening result is not Low/Medium)"
            )
        : null;

  const noteColor = missing
    ? "red"
    : screening.status === "NOT_LISTED"
      ? "red"
      : pass
        ? "green"
        : "orange";

  return { missing, listed, pass, badge, note, noteColor };
};

const pickHighestCheck = (checks, rows) => {
  let best = null;
  checks.forEach((check) => {
    const screening = screenCountryRisk(rows, check.country);
    if (!best || countryScreeningRank(screening) > countryScreeningRank(best.screening)) {
      best = { ...check, screening };
    }
  });
  return best;
};

const CheckRow = ({ check, screening, t }) => {
  const pass = screening.status === "PASS";
  const Icon = pass ? IconCircleCheck : IconAlertTriangle;
  const color = pass ? "green" : screening.status === "NOT_LISTED" ? "red" : "orange";

  return (
    <Group spacing="sm" noWrap>
      <Icon size={18} color={color} aria-label={pass ? t("Passed") : t("Needs handling")} />
      <Text size="sm" sx={{ flex: 1, minWidth: 0 }}>
        {t(check.source)}
      </Text>
      <Text size="sm" weight={600}>
        {check.country}
      </Text>
    </Group>
  );
};

const ClientDueDiligenceCountry = ({ form, error, label, required, readOnly = false }) => {
  const { t } = useTranslation();
  const [api] = useServerApi();
  const [rows, setRows] = useState(() => normalizeCountryRiskRatings());
  const checks = resolveClientDueDiligenceChecks(form?.values);
  const countryValue = dueDiligenceCountryValue(resolveClientDueDiligenceCountries(form?.values));
  const formRef = useRef(form);
  formRef.current = form;

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const result = await api.search({ apiEntity: "setting", pageSize: 1 });
        if (cancelled) return;
        setRows(normalizeCountryRiskRatings(result?.docs?.[0]?.countryRiskRatings));
      } catch (loadError) {
        console.log(loadError);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (readOnly) return;
    const currentForm = formRef.current;
    if (!currentForm?.setFieldValue || !currentForm?.values) return;
    if ((currentForm.values.dueDiligence?.country || "") === countryValue) return;
    const next = assignDueDiligenceCountry(currentForm.values, countryValue);
    currentForm.setFieldValue("dueDiligence", next.dueDiligence);
  }, [countryValue, readOnly]);

  const highest = pickHighestCheck(checks, rows);
  const screening = highest?.screening || { status: "EMPTY" };
  const view = screeningPresentation(screening, t);
  const title = label || t("Country");

  return (
    <DueDiligencePanel
      title={title}
      required={required}
      headline={view.missing ? null : highest.country}
      meta={view.missing ? null : t("Highest risk")}
      badge={
        view.badge ? (
          <DueDiligenceBadge color={view.badge.color}>{view.badge.label}</DueDiligenceBadge>
        ) : null
      }
      note={view.note}
      noteColor={view.noteColor}
    >
      {view.listed ? (
        <DueDiligenceFacts
          items={[
            {
              label: t("Country Risk"),
              value: <Text size="sm" weight={600}>{t(screening.label)}</Text>,
            },
            {
              label: t("Risk Score"),
              value: (
                <Text size="sm" weight={600}>
                  {formatRiskScore(screening.score, t)}
                </Text>
              ),
            },
            {
              label: t("Risk Level"),
              value: (
                <DueDiligenceBadge color={COUNTRY_RISK_LEVEL_COLOR[screening.level] || "gray"}>
                  {t(screening.level)}
                </DueDiligenceBadge>
              ),
            },
          ]}
        />
      ) : null}
      {checks.length ? (
        <Stack spacing="xs" mt={view.listed || view.note ? "md" : 0}>
          {checks.map((check, index) => (
            <CheckRow
              key={`${check.source}-${check.country}-${index}`}
              check={check}
              screening={screenCountryRisk(rows, check.country)}
              t={t}
            />
          ))}
        </Stack>
      ) : null}
      {error && view.missing ? (
        <Text size="xs" color="red" mt="xs">
          {error}
        </Text>
      ) : null}
    </DueDiligencePanel>
  );
};

export default ClientDueDiligenceCountry;
